import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Container } from '@mui/material';

export const authTextFieldSx = (isMobile) => ({
  mb: 3,
  '& .MuiInputBase-root': {
    background: 'var(--color-bg-input)',
    color: 'var(--color-text)',
  },
  '& .MuiInputLabel-root': {
    color: 'var(--color-text-secondary)',
    fontSize: isMobile ? '16px' : '14px'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--color-text-secondary)'
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: 'var(--color-text-secondary)'
  },
  '& .MuiInputBase-root.Mui-focused': {
    background: 'var(--color-bg-input-focused)'
  },
});

export const authSubmitButtonSx = (isMobile) => ({
  mt: 3,
  mb: 2,
  py: 1.5,
  fontSize: isMobile ? '20px' : '24px',
  fontWeight: 600,
  background: 'linear-gradient(135deg, var(--color-accent), var(--color-accent2))',
  border: 'none',
  borderRadius: '8px',
  textTransform: 'none',
  minHeight: isMobile ? '48px' : '52px',
  '&:hover': {
    background: 'var(--color-accent)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    background: 'var(--color-bg-disabled)',
    color: 'var(--color-text-disabled)'
  },
});

export const authTextLinkSx = (isMobile) => ({
  textTransform: 'none',
  color: 'var(--color-accent)',
  fontSize: isMobile ? '14px' : '13px',
  minHeight: isMobile ? '44px' : 'auto',
  px: 0,
  '&:hover': {
    background: 'transparent',
    textDecoration: 'underline'
  }
});

const AuthPageLayout = ({ title, subtitle, children, compact = false }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <Box
      className={compact ? 'login-page login-page-compact' : 'login-page'}
      sx={{
        minHeight: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
        padding: isMobile ? '16px' : '0'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, transparent 70%)',
          animation: 'float 30s ease-in-out infinite',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-50%',
          right: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle, rgba(0, 188, 212, 0.05) 0%, transparent 70%)',
          animation: 'float 35s ease-in-out infinite reverse',
          zIndex: 0
        }}
      />
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            minHeight: isMobile ? 'auto' : '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: isMobile ? 2 : 4
          }}
        >
          <Paper
            elevation={8}
            className="glass"
            sx={{
              p: isMobile ? 3 : 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: isMobile ? '100%' : 360,
              minHeight: compact ? 'auto' : (isMobile ? 'auto' : 500),
              borderRadius: 2,
              background: 'var(--color-bg-alt)',
              backdropFilter: 'blur(20px)',
              border: '1px solid var(--color-border)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.1)',
            }}
          >
            <Box
              sx={{
                mb: isMobile ? 3 : 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography
                component="h1"
                variant={isMobile ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 700,
                  color: 'var(--color-text)',
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: 'center',
                    color: 'var(--color-text-secondary)',
                    fontSize: isMobile ? '14px' : '16px'
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            {typeof children === 'function' ? children(isMobile) : children}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default AuthPageLayout;
